// Utility functions for data processing and visualization

// Format numbers for display
export const formatNumber = (num) => {
  if (num === null || num === undefined || isNaN(num)) return 'N/A';
  if (num >= 10000000) return (num / 10000000).toFixed(1) + ' Cr';
  if (num >= 100000) return (num / 100000).toFixed(1) + ' L';
  if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
  return num.toLocaleString();
};

// Format percentage
export const formatPercentage = (num, decimals = 1) => {
  if (num === null || num === undefined || isNaN(num)) return 'N/A';
  return `${num.toFixed(decimals)}%`;
};

// Calculate vote share
export const calculateVoteShare = (votes, totalVotes) => {
  if (!votes || !totalVotes || totalVotes === 0) return 0;
  return (votes / totalVotes) * 100;
};

// Calculate margin percentage
export const calculateMarginPercentage = (winnerVotes, runnerUpVotes, totalVotes) => {
  if (!winnerVotes || !runnerUpVotes || !totalVotes) return 0;
  const margin = winnerVotes - runnerUpVotes;
  return (margin / totalVotes) * 100;
};

// Generate color palette for charts
export const generateColorPalette = (count) => {
  const colors = [
    '#1976d2', '#388e3c', '#f57c00', '#d32f2f', '#7b1fa2',
    '#0097a7', '#5d4037', '#616161', '#e64a19', '#7cb342',
    '#fbc02d', '#e91e63', '#673ab7', '#009688', '#ff9800'
  ];
  
  return colors.slice(0, count);
};

// Party color mapping
export const getPartyColor = (partyName) => {
  const partyColors = {
    'BJP': '#ff9933',
    'Bharatiya Janata Party': '#ff9933',
    'Indian National Congress': '#00a86b',
    'INC': '#00a86b',
    'Congress': '#00a86b',
    'JD(U)': '#138808',
    'Janata Dal (United)': '#138808',
    'RJD': '#8B008B',
    'Rashtriya Janata Dal': '#8B008B',
    'AIMIM': '#0F7B0F',
    'BSP': '#0000FF',
    'Bahujan Samaj Party': '#0000FF',
    'SP': '#FF0000',
    'Samajwadi Party': '#FF0000',
    'CPI': '#FF0000',
    'CPI(M)': '#FF0000',
    'AITC': '#20B2AA',
    'All India Trinamool Congress': '#20B2AA',
    'TMC': '#20B2AA'
  };
  
  return partyColors[partyName] || '#757575';
};

// Sentiment analysis utilities
export const getSentimentColor = (sentiment) => {
  const sentimentColors = {
    positive: '#4caf50',
    neutral: '#ff9800', 
    negative: '#f44336',
    mixed: '#9c27b0'
  };
  
  return sentimentColors[sentiment?.toLowerCase()] || '#757575';
};

export const getSentimentIcon = (sentiment) => {
  const sentimentIcons = {
    positive: '👍',
    neutral: '😐',
    negative: '👎',
    mixed: '🤔'
  };
  
  return sentimentIcons[sentiment?.toLowerCase()] || '📊';
};

// Data validation utilities
export const validateData = (data, requiredFields) => {
  if (!data) return false;
  
  return requiredFields.every(field => {
    const fieldValue = getNestedValue(data, field);
    return fieldValue !== null && fieldValue !== undefined;
  });
};

export const getNestedValue = (obj, path) => {
  return path.split('.').reduce((current, key) => 
    current && current[key] !== undefined ? current[key] : null, obj
  );
};

// Data transformation for charts
export const transformDataForChart = (data, type = 'pie') => {
  if (!data || typeof data !== 'object') return [];
  
  switch (type) {
    case 'pie':
      return Object.entries(data).map(([name, value]) => ({
        name,
        value: typeof value === 'number' ? value : 0,
        color: generateColorPalette(Object.keys(data).length)[Object.keys(data).indexOf(name)]
      }));
      
    case 'bar':
      return Object.entries(data).map(([name, value]) => ({
        category: name,
        value: typeof value === 'number' ? value : 0
      }));
      
    case 'line':
      return Object.entries(data).map(([x, y], index) => ({
        x: isNaN(x) ? index : Number(x),
        y: typeof y === 'number' ? y : 0,
        label: x
      }));
      
    default:
      return data;
  }
};

// Generate mock sentiment data for demonstration
export const generateMockSentimentData = (constituency) => {
  // This would typically come from social media analysis APIs
  const basePositive = Math.floor(Math.random() * 20) + 35; // 35-55%
  const baseNegative = Math.floor(Math.random() * 15) + 20; // 20-35%
  const neutral = 100 - basePositive - baseNegative;
  
  return {
    positive: basePositive,
    neutral: neutral,
    negative: baseNegative
  };
};

// Generate mock opinion trends
export const generateMockOpinionTrends = (constituency) => {
  const commonIssues = [
    'Unemployment', 'Infrastructure', 'Healthcare', 'Education', 
    'Agriculture', 'Women\'s Safety', 'Corruption', 'Development',
    'Roads', 'Water Supply', 'Electricity', 'Law and Order'
  ];
  
  const selectedIssues = commonIssues
    .sort(() => 0.5 - Math.random())
    .slice(0, 6)
    .map(issue => ({
      issue,
      mentions: Math.floor(Math.random() * 30) + 15 // 15-45%
    }))
    .sort((a, b) => b.mentions - a.mentions);
  
  return {
    sentiment: generateMockSentimentData(constituency),
    issues: selectedIssues
  };
};

// Generate mock social media data
export const generateMockSocialMediaData = (constituency) => {
  const hashtags = [
    '#BiharElections', '#IndiaElections', '#Vote2024', '#Democracy',
    `#${constituency.name?.replace(/\s+/g, '')}`, '#Development',
    '#Youth', '#Jobs', '#Infrastructure', '#Change'
  ];
  
  return {
    hashtags: hashtags.slice(0, 5),
    mentions: Math.floor(Math.random() * 15000) + 5000,
    topPosts: [
      {
        user: '@politicalwatch',
        content: `${constituency.name} voters discuss key issues`,
        sentiment: 'neutral'
      }
    ]
  };
};

// Generate mock party strength data
export const generateMockPartyStrength = (winnerParty) => {
  const parties = ['BJP', 'INC', 'JD(U)', 'RJD', 'Others'];
  const strengths = {};
  
  let remaining = 100;
  
  parties.forEach((party, index) => {
    if (index === parties.length - 1) {
      strengths[party] = remaining;
    } else {
      const min = party === winnerParty ? 35 : 5;
      const max = party === winnerParty ? 55 : 25;
      const strength = Math.min(remaining - (parties.length - index - 1) * 5, 
                               Math.floor(Math.random() * (max - min)) + min);
      strengths[party] = strength;
      remaining -= strength;
    }
  });
  
  return strengths;
};

// Generate economic indicators
export const generateMockEconomicData = (constituency) => {
  const isUrban = constituency.name?.toLowerCase().includes('city') || 
                  constituency.name?.toLowerCase().includes('urban');
  
  return {
    gdp: (Math.random() * 3 + 1.5).toFixed(1), // 1.5-4.5%
    unemployment: (Math.random() * 4 + (isUrban ? 5 : 7)).toFixed(1), // Urban: 5-9%, Rural: 7-11%
    povertyRate: Math.floor(Math.random() * 15 + (isUrban ? 10 : 20)) // Urban: 10-25%, Rural: 20-35%
  };
};

// Generate forecasting data
export const generateMockForecasts = (constituency, partyStrength) => {
  const leadingParty = Object.entries(partyStrength)
    .sort(([,a], [,b]) => b - a)[0][0];
  
  const winProbability = Math.floor(Math.random() * 25) + 60; // 60-85%
  
  const scenarios = [
    {
      scenario: 'Development promises delivered',
      impact: `+${Math.floor(Math.random() * 5) + 3}% ${leadingParty}`
    },
    {
      scenario: 'Opposition alliance strengthens',
      impact: `-${Math.floor(Math.random() * 6) + 4}% ${leadingParty}`
    }
  ];
  
  return {
    leading: leadingParty,
    winProbability,
    scenarios
  };
};

// Export utility functions
export const dataUtils = {
  formatNumber,
  formatPercentage,
  calculateVoteShare,
  calculateMarginPercentage,
  generateColorPalette,
  getPartyColor,
  getSentimentColor,
  getSentimentIcon,
  validateData,
  getNestedValue,
  transformDataForChart,
  generateMockSentimentData,
  generateMockOpinionTrends,
  generateMockSocialMediaData,
  generateMockPartyStrength,
  generateMockEconomicData,
  generateMockForecasts
};
